import React, { useEffect } from 'react';
import { Shield, Lock, Eye, Database, Mail } from 'lucide-react';
import SharedNavigation from './SharedNavigation';
import './SharedStyles.css';

const Privacy: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="app-page">
      <SharedNavigation />

      <main className="app-main">
        <div className="app-container-narrow">
          <div className="text-center mb-6">
            <h1 className="app-title">Datenschutzerklärung</h1>
            <p className="app-subtitle">
              Transparenz und Schutz Ihrer Daten haben für uns höchste Priorität
            </p>
          </div>

          {/* Quick Overview */}
          <div className="app-content" style={{ marginBottom: '2rem' }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Shield size={24} style={{ color: 'var(--voidmind-primary)' }} />
              Datenschutz auf einen Blick
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginTop: '1.5rem' }}>
              <div style={{ padding: '1.5rem', background: 'var(--background-light)', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Lock size={20} style={{ color: 'var(--success-green)' }} />
                  <strong>Keine API-Inhalte gespeichert</strong>
                </div>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>Wir speichern niemals den Inhalt Ihrer API-Anfragen</p>
              </div>
              <div style={{ padding: '1.5rem', background: 'var(--background-light)', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Eye size={20} style={{ color: 'var(--success-green)' }} />
                  <strong>Kein Tracking</strong>
                </div>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>Keine Cookies oder Analysedienste von Drittanbietern</p>
              </div>
              <div style={{ padding: '1.5rem', background: 'var(--background-light)', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <Database size={20} style={{ color: 'var(--voidmind-primary)' }} />
                  <strong>DSGVO-konform</strong>
                </div>
                <p style={{ margin: 0, fontSize: '0.9rem' }}>Vollständige Einhaltung der EU-Datenschutzverordnung</p>
              </div>
            </div>
          </div>
          
          <div className="app-content">
            <section>
              <h2>1. Datenschutz auf einen Blick</h2>
              <h3>Allgemeine Hinweise</h3>
              <p>
                Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren 
                personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene 
                Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
              </p>
            </section>

            <section>
              <h2>2. Datenerfassung auf dieser Website</h2>
              <h3>Wer ist verantwortlich für die Datenerfassung auf dieser Website?</h3>
              <p>
                Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. 
                Dessen Kontaktdaten können Sie dem Abschnitt „Hinweis zur Verantwortlichen Stelle" 
                in dieser Datenschutzerklärung entnehmen.
              </p>

              <h3>Wie erfassen wir Ihre Daten?</h3>
              <p>
                Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. 
                Hierbei kann es sich z. B. um Daten handeln, die Sie in ein Kontaktformular eingeben.
              </p>
            </section>

            <section>
              <h2>3. API-Nutzung und Logging</h2>
              <h3>Keine Speicherung von API-Anfragen</h3>
              <p>
                Wir speichern grundsätzlich keine Inhalte Ihrer API-Anfragen. Zur Abrechnung 
                und Missbrauchserkennung werden lediglich folgende Metadaten erfasst:
              </p>
              <ul>
                <li>Zeitpunkt der Anfrage</li>
                <li>Verwendeter API-Token</li>
                <li>HTTP-Status-Code der Antwort</li>
                <li>IP-Adresse (anonymisiert nach 24 Stunden)</li>
              </ul>
            </section>

            <section>
              <h2>4. Cookies und Tracking</h2>
              <p>
                Diese Website verwendet nur technisch notwendige Cookies für die Funktionalität 
                der Anwendung. Wir verwenden keine Tracking-Cookies oder Analysedienste von Drittanbietern.
              </p>
            </section>

            <section>
              <h2>5. Zahlungsabwicklung</h2>
              <p>
                Für die Zahlungsabwicklung nutzen wir Stripe. Dabei werden Ihre Zahlungsdaten 
                direkt an Stripe übertragen und nicht auf unseren Servern gespeichert. 
                Weitere Informationen finden Sie in der <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
                  Datenschutzerklärung von Stripe
                </a>.
              </p>
            </section>

            <section>
              <h2>6. Ihre Rechte</h2>
              <p>Sie haben jederzeit das Recht:</p>
              <ul>
                <li>Auskunft über Ihre bei uns gespeicherten personenbezogenen Daten zu erhalten</li>
                <li>Berichtigung unrichtiger personenbezogener Daten zu verlangen</li>
                <li>Löschung Ihrer bei uns gespeicherten personenbezogenen Daten zu verlangen</li>
                <li>Einschränkung der Datenverarbeitung zu verlangen</li>
                <li>Widerspruch gegen die Verarbeitung Ihrer Daten einzulegen</li>
                <li>Datenübertragbarkeit zu verlangen</li>
              </ul>
            </section>

            <section>
              <h2>7. Kontakt</h2>
              <p>
                Bei Fragen zum Datenschutz wenden Sie sich bitte an: <a href="mailto:privacy@pfaendungstabelle.voidmind.io">privacy@pfaendungstabelle.voidmind.io</a>
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Privacy;
